"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

/* ── Settings.Page ─────────────────────────────────────────────────── */

interface PageProps {
  children: React.ReactNode;
  maxWidth?: string;
  className?: string;
}

function Page({ children, maxWidth = "770px", className }: PageProps) {
  return (
    <div className={cn("flex flex-col items-center lg:px-4 lg:py-6 sm:p-6", className)}>
      <div className="w-full py-3 lg:py-12" style={{ maxWidth }}>
        {children}
      </div>
    </div>
  );
}

/* ── Settings.Header ───────────────────────────────────────────────── */

interface HeaderProps {
  title: string;
  description?: React.ReactNode;
  /** Optional control aligned to the trailing edge of the title row —
   * for page-level filters or a primary action. */
  action?: React.ReactNode;
}

function Header({ title, description, action }: HeaderProps) {
  return (
    <>
      <div className="flex items-start justify-between gap-3 max-md:p-3 max-md:px-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h1 className="text-lg font-medium text-heading">{title}</h1>
          {description && (
            <p className="text-sm text-contrast/50">{description}</p>
          )}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <Separator className="mt-4" />
    </>
  );
}

/* ── Settings.Body ─────────────────────────────────────────────────── */

const SPRING = { type: "spring", damping: 28, stiffness: 380, mass: 0.5 } as const;

interface BodyProps {
  children: React.ReactNode;
  gap?: number;
  className?: string;
}

function Body({ children, gap = 8, className }: BodyProps) {
  return (
    <motion.div
      className={cn("mt-4 lg:mt-6 flex flex-col max-md:px-4 max-md:pb-2", className)}
      style={{ gap: `${gap * 4}px` }}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={SPRING}
    >
      {children}
    </motion.div>
  );
}

/* ── Settings.Section ──────────────────────────────────────────────── */

interface SectionProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

function Section({ title, description, children, className }: SectionProps) {
  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {title && <SectionHeading title={title} description={description} />}
      {children}
    </div>
  );
}

/* ── Settings.SectionHeading ───────────────────────────────────────── */

interface SectionHeadingProps {
  title: string;
  description?: string;
}

function SectionHeading({ title, description }: SectionHeadingProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm font-medium text-heading leading-5">
        {title}
      </span>
      {description && (
        <span className="text-xs text-contrast/60">{description}</span>
      )}
    </div>
  );
}

/* ── Settings.Separator ────────────────────────────────────────────── */

interface SeparatorProps {
  className?: string;
}

function Separator({ className }: SeparatorProps) {
  return <div className={cn("h-px w-full bg-contrast/10", className)} />;
}

/* ── Namespace export ──────────────────────────────────────────────── */

export const Settings = Object.assign(
  {},
  {
    Page,
    Header,
    Body,
    Section,
    SectionHeading,
    Separator,
  },
);
